import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatContext } from '../../../../Chat.hook.ts';
import './ChatInfoLeadsalesAI.styles.scss';
import LeadsalesAIItem from './components/LeadsalesAIItem.component.tsx';

export default function ChatInfoLeadsalesAI(): JSX.Element {
  const { leadsalesAI, setSelectedNote } = useChatContext();
  const { t } = useTranslation(['chat/chat']);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isActive || isEmpty(leadsalesAI)) return;

      e.stopPropagation();

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, leadsalesAI.length - 1));
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      }

      if (e.key === 'Enter' && leadsalesAI.length > 0) {
        e.preventDefault();
        const selectedAI = leadsalesAI[selectedIndex];
        if (!selectedAI) return;

        setSelectedNote({
          id: selectedAI.id,
          title: selectedAI.note?.title || '',
          content: selectedAI.content?.text || '',
          attachments: selectedAI.content?.attachments || undefined,
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [leadsalesAI, selectedIndex, isActive]);

  // Ensure the selected AI item is always visible
  useEffect(() => {
    itemsRef.current[selectedIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }, [selectedIndex]);

  // Reset the index if the list of AI items changes
  useEffect(() => {
    setSelectedIndex((prev) => Math.min(prev, leadsalesAI.length - 1));
  }, [leadsalesAI.length]);

  return (
    <>
      {isEmpty(leadsalesAI) ? (
        <div className="ChatInfoLeadsalesAI__Content">
          <p className="ChatInfoLeadsalesAI__EmptyState">
            {t('leadsalesAI.noContent')}
          </p>
        </div>
      ) : (
        <div
          className="ChatInfoLeadsalesAI__Items"
          onMouseEnter={() => setIsActive(true)}
          onMouseLeave={() => setIsActive(false)}
        >
          {leadsalesAI.map((aiItem, index) => (
            <LeadsalesAIItem
              ref={(el: HTMLDivElement | null) => {
                if (el) itemsRef.current[index] = el;
              }}
              key={aiItem.id}
              index={index}
              selectedIndex={selectedIndex}
              {...aiItem}
            />
          ))}
        </div>
      )}
    </>
  );
}
